/**
 * DRAFT SPLIT ORDERS DIALOG
 * Displays all draft/pending split table orders and allows resuming them
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as ResumeIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  TableRestaurant as TableIcon,
  People as PeopleIcon,
  Refresh
} from '@mui/icons-material';
import SplitTableDraftService, { SplitTableDraft } from '../services/splitTableDraftService';
import { useLanguage } from '../contexts/LanguageContext';

interface DraftSplitOrdersDialogProps {
  open: boolean;
  onClose: () => void;
  onResume: (draft: SplitTableDraft) => void;
}

const DraftSplitOrdersDialog: React.FC<DraftSplitOrdersDialogProps> = ({
  open,
  onClose,
  onResume
}) => {
  const [drafts, setDrafts] = useState<SplitTableDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const draftService = SplitTableDraftService.getInstance();
  const { t, language } = useLanguage();

  // Load drafts when dialog opens
  useEffect(() => {
    if (open) {
      loadDrafts();
      
      // Subscribe to real-time updates
      const unsubscribe = draftService.subscribeToActiveDrafts((updatedDrafts) => {
        setDrafts(updatedDrafts);
      });
      
      return () => unsubscribe();
    }
  }, [open]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📋 [DRAFTS DIALOG] Loading drafts...');
      
      const activeDrafts = await draftService.getActiveDrafts();
      
      console.log('📋 [DRAFTS DIALOG] Received', activeDrafts.length, 'drafts from service');
      console.log('📋 [DRAFTS DIALOG] Draft details:', activeDrafts.map(d => ({
        id: d.id,
        table_number: d.table_number,
        status: d.status,
        people_count: d.people?.length || 0
      })));
      
      setDrafts(activeDrafts);
      
      if (activeDrafts.length === 0) {
        console.warn('⚠️ [DRAFTS DIALOG] No active drafts found. This could mean:');
        console.warn('  1. All drafts have been completed or cancelled');
        console.warn('  2. No drafts exist in Firestore');
        console.warn('  3. Drafts exist but have wrong status (not "draft" or "pending")');
        
        // Debug: Check all drafts to see what's in the database
        try {
          const allDrafts = await draftService.getAllDrafts();
          if (allDrafts.length > 0) {
            console.warn('⚠️ [DRAFTS DIALOG] Found', allDrafts.length, 'total drafts in database:');
            allDrafts.forEach(draft => {
              console.warn(`  - Draft ID: ${draft.id}, Table: ${draft.table_number}, Status: ${draft.status}`);
            });
            console.warn('⚠️ [DRAFTS DIALOG] These drafts are not showing because their status is not "draft" or "pending"');
          } else {
            console.warn('⚠️ [DRAFTS DIALOG] No drafts exist in Firestore at all');
          }
        } catch (debugError) {
          console.error('❌ [DRAFTS DIALOG] Error during debug check:', debugError);
        }
      }
    } catch (err: any) {
      console.error('❌ [DRAFTS DIALOG] Error loading drafts:', err);
      console.error('❌ [DRAFTS DIALOG] Error details:', {
        code: err?.code,
        message: err?.message,
        stack: err?.stack
      });
      setError(t('drafts.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResume = (draft: SplitTableDraft) => {
    onResume(draft);
    onClose();
  };

  const handleCancel = async (draftId: string) => {
    if (window.confirm(t('drafts.confirm_delete'))) {
      try {
        await draftService.cancelDraft(draftId);
        await loadDrafts();
      } catch (err) {
        console.error('❌ [DRAFTS] Error cancelling draft:', err);
        alert(t('drafts.delete_error'));
      }
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStepName = (step: number) => {
    const steps = [
      t('drafts.step_create'),
      t('drafts.step_assign'),
      t('drafts.step_payment'),
      t('drafts.step_complete')
    ];
    return steps[step] || 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status === 'draft' ? t('drafts.status_draft') : t('drafts.status_pending');
  };

  if (!open) return null;

  return (
    <>
      {/* Full Screen Modal - Same Style as Full Order Details */}
      <Box
        data-modal-overlay="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'auto'
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '2px solid #000000',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <Box sx={{ 
            bgcolor: '#f8f9fa', 
            p: 2, 
            borderTopLeftRadius: 12, 
            borderTopRightRadius: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('drafts.title')}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* Content */}
          <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>{t('drafts.loading')}</Typography>
          </Box>
        ) : drafts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TableIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('drafts.no_drafts')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('drafts.no_drafts_desc')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {drafts.map((draft) => (
              <Grid item xs={12} key={draft.id}>
                <Card sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TableIcon sx={{ color: '#1976d2' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('drafts.table')} {draft.table_number}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(draft.status)} 
                            size="small" 
                            color={getStatusColor(draft.status)}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('drafts.step')}: {draft.active_step + 1}/4 - {getStepName(draft.active_step)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 16 }} />
                          {formatDate(draft.updated_at || draft.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ color: '#666', fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('drafts.people')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {draft.people.length}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('drafts.total_amount')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            €{draft.original_order_total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('drafts.created_by')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {draft.created_by || 'POS System'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('drafts.order_id')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {draft.order_id || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {draft.people.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          {t('drafts.people_label')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {draft.people.map((person, index) => (
                            <Chip
                              key={person.id || index}
                              label={`${person.name} (€${(person.total || 0).toFixed(2)})`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => draft.id && handleCancel(draft.id)}
                    >
                      {t('drafts.delete')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<ResumeIcon />}
                      onClick={() => handleResume(draft)}
                      sx={{
                        backgroundColor: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#b71c1c'
                        }
                      }}
                    >
                      {t('drafts.resume')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
          </Box>
          
          {/* Footer */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}>
            <Button 
              onClick={onClose} 
              variant="outlined"
              sx={{
                borderColor: '#e0e0e0',
                color: '#333',
                '&:hover': {
                  borderColor: '#b0b0b0',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {t('common.close')}
            </Button>
            <Button 
              onClick={loadDrafts} 
              variant="outlined" 
              startIcon={<Refresh />}
              sx={{
                borderColor: '#e0e0e0',
                color: '#333',
                '&:hover': {
                  borderColor: '#b0b0b0',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {t('drafts.refresh')}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DraftSplitOrdersDialog;

